/**
 * Project-global track header/column tints (index into ROW_MARKER_COLORS).
 * `null` = unset (no tint).
 */
import {
  clampRowMarkerColor,
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  MAX_INSTRUMENT_SLOT_COUNT,
  ROW_MARKER_COLORS,
  rowMarkerColorHex,
} from "../../../../src/lib/audio";

export type TrackColor = number | null;

export function createDefaultTrackColors(trackCount = DEFAULT_INSTRUMENT_SLOT_COUNT): TrackColor[] {
  const n = Math.min(
    MAX_INSTRUMENT_SLOT_COUNT,
    Math.max(1, Math.round(trackCount) || DEFAULT_INSTRUMENT_SLOT_COUNT),
  );
  return Array.from({ length: n }, () => null);
}

/** Normalize persisted / imported track color arrays to length `trackCount`. */
export function normalizeTrackColors(raw: unknown, trackCount: number): TrackColor[] {
  const n = Math.min(
    MAX_INSTRUMENT_SLOT_COUNT,
    Math.max(1, Math.round(trackCount) || DEFAULT_INSTRUMENT_SLOT_COUNT),
  );
  const src = Array.isArray(raw) ? raw : [];
  return Array.from({ length: n }, (_, i) => {
    const value = src[i];
    if (value == null || value === false) {
      return null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return clampRowMarkerColor(value);
    }
    return null;
  });
}

export function trackColorHex(color: TrackColor | undefined): string | undefined {
  if (color == null) {
    return undefined;
  }
  return rowMarkerColorHex(color);
}

/** Soft mix for arrange matrix cells / headers. */
export function trackTintBackground(base: string, color: TrackColor | undefined, amount = 28): string {
  const hex = trackColorHex(color);
  if (!hex) {
    return base;
  }
  return `color-mix(in srgb, ${hex} ${amount}%, ${base})`;
}

export { ROW_MARKER_COLORS, clampRowMarkerColor };
